import React, { useEffect } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter, ModalContent, Button, Card, CardBody, Divider } from '@heroui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faClock, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { IUserGuideModalProps } from '../userGuideTypes';
import { USER_GUIDE_VIDEOS } from '../userGuideConfig';
import { trackEvent } from '../../../services/application-insights/appInsights';
import { AppInsightsActions, AppInsightsComponents } from '../../../services/application-insights/appInsightsConstants';

const UserGuideModal: React.FC<IUserGuideModalProps> = ({ isOpen, onOpenChange, onClose }) => {
	// Get the single video from configuration
	const video = USER_GUIDE_VIDEOS[0]; // Since we now have only one video

	// Track modal open event
	useEffect(() => {
		if (isOpen) {
			trackEvent('user_guide_modal_opened', {
				component: AppInsightsComponents.USER_GUIDE_MODAL,
				action: AppInsightsActions.USER_GUIDE_MODAL_OPEN,
				videoId: video?.id,
				videoTitle: video?.title,
			});
		}
	}, [isOpen, video]);

	const handleModalClose = () => {
		onClose();

		trackEvent('user_guide_modal_closed', {
			component: AppInsightsComponents.USER_GUIDE_MODAL,
			action: AppInsightsActions.USER_GUIDE_MODAL_CLOSE,
			videoId: video?.id,
			videoTitle: video?.title,
		});
	};

	if (!video) {
		return (
			<Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleModalClose} className="rounded" size="lg">
				<ModalContent>
					<ModalHeader>
						<h2>User Guide</h2>
					</ModalHeader>
					<ModalBody>
						<p>No video content available at the moment.</p>
					</ModalBody>
					<ModalFooter>
						<Button size="sm" color="danger" className="rounded" onPress={handleModalClose}>
							Close
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			onClose={handleModalClose}
			className="rounded"
			size="5xl"
			scrollBehavior="inside"
			classNames={{
				base: 'max-h-[95vh]',
				body: 'px-4',
				closeButton: 'rounded',
			}}
		>
			<ModalContent>
				{() => (
					<>
						<ModalHeader className="flex items-center gap-2">
							<FontAwesomeIcon icon={faVideo} className="text-msi-orange-color" />
							<h2 className="text-2xl font-bold">User Guide</h2>
						</ModalHeader>

						<ModalBody>
							<div className="space-y-2">
								{/* Video Player */}
								<div className="aspect-video bg-black rounded overflow-hidden shadow-lg">
									<iframe
										src={video.videoUrl}
										width="100%"
										height="100%"
										allowFullScreen={true}
										className="w-full h-full"
										title={video.title}
									/>
								</div>

								{/* Video Information */}
								<Card className='rounded'>
									<CardBody className="space-y-2 rounded">
										<div className="space-y-2">
											<h3 className="text-lg font-semibold flex items-center gap-2">
												<FontAwesomeIcon icon={faInfoCircle} className="text-msi-orange-color text-sm" />
												About This Video
											</h3>
											<p className="text-gray-600 text-sm leading-relaxed">{video.description}</p>
										</div>

										{video.duration && (
											<>
												<Divider />

												<div className="flex gap-6 text-sm text-gray-600">
													<span className="flex items-center gap-2">
														<FontAwesomeIcon icon={faClock} className="text-gray-500" />
														<strong>Duration:</strong> {video.duration}
													</span>
												</div>
											</>
										)}
									</CardBody>
								</Card>
							</div>
						</ModalBody>

						<ModalFooter>
							<Button size="sm" color="danger" className="rounded" onPress={handleModalClose}>
								Close
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
};

export default UserGuideModal;
